import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';

export function PassToken({ onSessionReady }: { onSessionReady?: () => void }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const codeVerifier = document.cookie
      .split('; ')
      .find(cookie => cookie.startsWith('code_verifier='))
      ?.split('=')[1];

    // Clean up the URL
    window.history.replaceState({}, document.title, "/pass-token");

    if (!code || !codeVerifier) {
      console.error('❌ Missing code or code_verifier');
      setStatus('error');
      return;
    }

    const tokenEndpoint = 'https://auth.heartcheckapp.com/oauth2/token';
    const clientId = 'kklrt877v19o9p8eaqr0ufjsb';
    const redirectUri = 'https://chat.heartcheckapp.com/pass-token';
    const region = process.env.REACT_APP_REGION;
    const userPoolId = process.env.REACT_APP_COGNITOPOOL;

    if (!region || !userPoolId) {
      console.error('❌ Missing REACT_APP_REGION or REACT_APP_COGNITOPOOL');
      setStatus('error');
      return;
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      redirect_uri: redirectUri,
      code,
      code_verifier: codeVerifier,
    });

    fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then(async (res) => {
        const tokens = await res.json();

        if (tokens.access_token && tokens.id_token) {
          // ✅ Store in localStorage
          localStorage.setItem('access_token', tokens.access_token);
          localStorage.setItem('id_token', tokens.id_token);

          // ✅ Also store in cookies for server access
          const cookieSettings = "Secure; SameSite=None; Path=/; Max-Age=3600";
          document.cookie = `id_token=${tokens.id_token}; ${cookieSettings}`;
          document.cookie = `access_token=${tokens.access_token}; ${cookieSettings}`;

          // Authenticate with Amplify
          const provider = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
          await Auth.federatedSignIn(
            provider,
            {
              token: tokens.id_token,
              expires_at: Date.now() + tokens.expires_in * 1000,
            },
            { name: 'CognitoUser' }
          );

          try {
            await Auth.currentSession();
            onSessionReady?.();
            sessionStorage.setItem("just-signed-in", "true");
            navigate('/chat');
          } catch (err) {
            console.error("❌ Failed to confirm session:", err);
            setStatus('error');
          }
        } else {
          console.error('❌ Token exchange failed:', tokens);
          setStatus('error');
        }
      })
      .catch((err) => {
        console.error('❌ Token exchange error:', err);
        setStatus('error');
      });
  }, [navigate, onSessionReady]);

  if (status === 'error') {
    return <div className="p-4 text-center text-xl text-red-600">Sign-in failed. Please try again.</div>;
  }

  return <div className="p-4 text-center text-xl">Signing you in...</div>;
}
